import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
import { getTableHeadings } from '@/store-selectors';
import DataTableHeaders from '@/containers/DataTableHeaders';
import { useAppSelector } from '@/store/hooks';
import { selectIsLoadingColumns } from '@/features/query-builder/queryBuilderSlice';
import { NoResultsFooter } from './NoResultsFooter';
import { useDrilldownContext } from '../context';

export const Table: React.FC<any> = ({
  children,
  className,
  dataTable,
  hasOptions,
}) => {
  const headerHeight = useCalculateMarginOffset();

  const shouldApplyOffset = !className.includes('expanded-cell');

  const style = shouldApplyOffset
    ? headerHeight
      ? { marginTop: `-${headerHeight}px` }
      : { visibility: 'hidden' }
    : undefined;

  return (
    <table className={className} style={style as React.CSSProperties}>
      <tbody>
        {dataTable.rows().length > 0 && (
          <DataTableHeaders
            headings={dataTable._headings}
            hasOptions={hasOptions}
          />
        )}
        {children}
      </tbody>
      <NoResultsFooter dataTable={dataTable} />
    </table>
  );
};

const useCalculateMarginOffset = () => {
  const headings = useSelector(getTableHeadings);
  const isLoading = useAppSelector(selectIsLoadingColumns);
  const headerRef = useDrilldownContext();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [headerHeight, setHeaderHeight] = React.useState(0);
  // if headers have loaded, calculate height of headers (sometimes they have scrollbars and sometimes not). Value is used to set margin-top of non-sticky table headers (that appear underneath).
  React.useLayoutEffect(() => {
    timeoutRef.current = setTimeout(() => {
      if (headerRef?.current) {
        const headerRefHeight = headerRef.current.clientHeight;
        if (headerRefHeight !== headerHeight) {
          setHeaderHeight(headerRefHeight);
        }
      }
    });

    return () => clearTimeout(timeoutRef.current as NodeJS.Timeout);
  }, [headings, isLoading]);

  return headerHeight;
};
